import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/*
 * Figma UI kit → Input field (806:5958): grey border, brand border on hover and focus, red on error. The text is
 * 16 px so iOS Safari does not zoom the page on focus.
 */
const FIELD =
  "h-12 w-full rounded-xl border border-line bg-surface px-4 text-base transition-colors hover:border-brand-500";
const FIELD_STATES =
  "aria-invalid:border-danger aria-invalid:hover:border-danger disabled:opacity-60 disabled:hover:border-line";

/**
 * Text field. `start` / `end` are the Figma «action button» slots inside the field (+998, «сум», an icon or a
 * button); with them `className` styles the outer frame.
 */
export function Input({
  className,
  start,
  end,
  ...props
}: ComponentProps<"input"> & { start?: ReactNode; end?: ReactNode }) {
  if (!start && !end) {
    return (
      <input
        className={cn(
          FIELD,
          FIELD_STATES,
          "outline-none placeholder:text-muted focus:border-brand-600",
          className,
        )}
        {...props}
      />
    );
  }
  return (
    <div
      className={cn(
        FIELD,
        "flex items-center gap-2 focus-within:border-brand-600",
        "has-aria-invalid:border-danger has-aria-invalid:hover:border-danger",
        "has-disabled:opacity-60 has-disabled:hover:border-line",
        className,
      )}
    >
      {start && <span className="flex shrink-0 items-center text-ink-toned">{start}</span>}
      <input
        className="h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted"
        {...props}
      />
      {end && <span className="flex shrink-0 items-center text-ink-toned">{end}</span>}
    </div>
  );
}
